import React, { createContext, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, normalizeLanguageCode } from '@/lib/i18n';

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => Promise<void>;
  direction: 'ltr' | 'rtl';
  supportedLanguages: typeof SUPPORTED_LANGUAGES;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: async () => {},
  direction: 'ltr',
  supportedLanguages: SUPPORTED_LANGUAGES
});

export function useLanguageContext() {
  return useContext(LanguageContext);
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();

  const readStoredLanguage = (): string => {
    if (typeof window === 'undefined') return 'en';
    const raw = window.localStorage.getItem('i18nextLng');
    if (!raw) return 'en';

    // Migrate accidental JSON-encoded values: "hr" -> hr
    const parsed = raw.startsWith('"') && raw.endsWith('"') ? raw.slice(1, -1) : raw;
    return normalizeLanguageCode(parsed);
  };

  useEffect(() => {
    const normalizedStoredLanguage = readStoredLanguage();
    const normalizedCurrentLanguage = normalizeLanguageCode(i18n.resolvedLanguage || i18n.language || 'en');

    if (typeof window !== 'undefined') {
      window.localStorage.setItem('i18nextLng', normalizedStoredLanguage);
    }

    if (normalizedStoredLanguage && normalizedStoredLanguage !== normalizedCurrentLanguage) {
      void i18n.changeLanguage(normalizedStoredLanguage);
    }
  }, [i18n]);

  const activeLanguage = normalizeLanguageCode(i18n.resolvedLanguage || i18n.language || 'en');

  useEffect(() => {
    document.documentElement.lang = activeLanguage;
    document.documentElement.dir = i18n.dir(activeLanguage);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem('i18nextLng', activeLanguage);
    }
  }, [activeLanguage, i18n]);

  const setLanguage = async (lang: string) => {
    try {
      const normalizedLanguage = normalizeLanguageCode(lang);
      await i18n.changeLanguage(normalizedLanguage);
      document.documentElement.lang = normalizedLanguage;
      document.documentElement.dir = i18n.dir(normalizedLanguage);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('i18nextLng', normalizedLanguage);
      }
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  const value = {
    language: activeLanguage,
    setLanguage,
    direction: i18n.dir(activeLanguage) as 'ltr' | 'rtl',
    supportedLanguages: SUPPORTED_LANGUAGES
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}